import fontkit from "@pdf-lib/fontkit"
import { PDFDocument, PDFFont } from "pdf-lib"

/**
 * Font loading/embedding for PDF generation.
 * Font files are copied verbatim from the iOS bundle (Resources/Assets/Fonts)
 * and served from public/pdf-assets/fonts so they load only when the builder opens.
 */

export type TPdfFontName =
	| "Lato-Regular"
	| "Lato-Bold"
	| "Lato-Italic"
	| "Lato-Light"
	| "Lato-Medium"
	| "Lato-Semibold"
	| "Lato-SemiboldItalic"
	| "BeausiteText-Light"
	| "BeausiteText-Clear"
	| "BeausiteDetail-Clear"
	| "BeausiteDetail-ClearItalic"
	| "BentonSans-Regular"

const fontFiles: Record<TPdfFontName, string> = {
	"Lato-Regular": "Lato-Regular.ttf",
	"Lato-Bold": "Lato-Bold.ttf",
	"Lato-Italic": "Lato-Italic.ttf",
	"Lato-Light": "Lato-Light.ttf",
	"Lato-Medium": "Lato-Medium.ttf",
	"Lato-Semibold": "Lato-Semibold.ttf",
	"Lato-SemiboldItalic": "Lato-SemiboldItalic.ttf",
	"BeausiteText-Light": "BeausiteText-Light.otf",
	"BeausiteText-Clear": "BeausiteText-Clear.otf",
	"BeausiteDetail-Clear": "BeausiteDetail-Clear.otf",
	"BeausiteDetail-ClearItalic": "BeausiteDetail-ClearItalic.otf",
	"BentonSans-Regular": "BentonSans-Regular.otf",
}

const fontBytesCache = new Map<TPdfFontName, Promise<ArrayBuffer>>()

export function loadFontBytes(name: TPdfFontName): Promise<ArrayBuffer> {
	const cached = fontBytesCache.get(name)
	if (cached) {
		return cached
	}
	const promise = fetch(`${process.env.PUBLIC_URL}/pdf-assets/fonts/${fontFiles[name]}`).then((res) => {
		if (!res.ok) {
			fontBytesCache.delete(name)
			throw new Error(`Failed to load font ${name}: ${res.status}`)
		}
		return res.arrayBuffer()
	})
	fontBytesCache.set(name, promise)
	return promise
}

/** 'OTTO' magic = OpenType with CFF outlines. */
function isCffFont(bytes: ArrayBuffer): boolean {
	const view = new Uint8Array(bytes)
	return view.length > 4 && view[0] === 0x4f && view[1] === 0x54 && view[2] === 0x54 && view[3] === 0x4f
}

/**
 * Embeds a set of fonts into a document. Each document needs its own
 * embedded PDFFont objects (they are per-document), but the raw bytes are cached.
 *
 * CFF (OTF) fonts are embedded WHOLE: fontkit's CFF subsetter produces
 * subsets that pdf.js tolerates but CoreGraphics (macOS Preview / Quick Look)
 * cannot decode — text renders as garbage symbols. TrueType fonts keep
 * subsetting (that code path is reliable and Lato is large).
 */
export async function embedFonts<T extends TPdfFontName>(
	doc: PDFDocument,
	names: readonly T[]
): Promise<Record<T, PDFFont>> {
	doc.registerFontkit(fontkit)
	const entries = await Promise.all(
		names.map(async (name) => {
			const bytes = await loadFontBytes(name)
			const subset = !isCffFont(bytes)
			const font = await doc.embedFont(new Uint8Array(bytes), { subset })
			if (subset) {
				// fontkit subsetters can crash on EMPTY subsets (fonts embedded but
				// never drawn) — seed every subset with one glyph to keep it valid
				font.encodeText(" ")
			}
			return [name, font] as const
		})
	)
	const result = {} as Record<T, PDFFont>
	entries.forEach(([name, font]) => {
		result[name] = font
	})
	return result
}
